import { t, type Locale } from '@sd/i18n';
import type { TechId } from '@sd/content';
import type { Command } from '@sd/sim';
import type { DiploAction, DiplomacyView } from './diplomacyModel';
import type { FleetAction, FleetView } from './fleetModel';
import { escapeHtml, num } from './format';
import { shipIcon } from './unitsBar';
import type { ResearchView, TechNode } from './researchModel';

/**
 * Research, fleet and diplomacy tabs of the side panel (#58), drawn from
 * their pure views. Strings only: the panel owns the DOM and the clicks.
 */
export const cmd = (c: Command) => escapeHtml(JSON.stringify(c));

const goodIcon = (g: string) => `<i class="sd-good-icon" data-good="${g}"></i>`;

/** A button that sends `command`, or a greyed one with the reason under it. */
export function actionButton(
  label: string,
  command: Command | null,
  reason: string | null,
  kind: 'primary' | 'heat' | '' = '',
  note = '',
): string {
  const cls = kind ? `sd-btn--${kind}` : '';
  return `<span class="sd-offer__action">${
    command
      ? `<button class="sd-btn ${cls}" type="button" data-command="${cmd(command)}">${escapeHtml(label)}</button>`
      : `<button class="sd-btn" type="button" disabled>${escapeHtml(label)}</button>`
  }${reason ? `<small class="sd-offer__why">${escapeHtml(reason)}</small>` : note ? `<small class="sd-label">${escapeHtml(note)}</small>` : ''}</span>`;
}

const act = (a: FleetAction | DiploAction) =>
  actionButton(
    a.label,
    a.command,
    a.reason,
    a.primary ? 'primary' : a.heat ? 'heat' : '',
    'warning' in a ? (a.warning ?? '') : '',
  );

/**
 * What is under way, pinned over a tab's scrolling list (#115, the author's
 * request): a name, the time left, a bar you can see, and an action.
 */
/** A line of work under way, filled as far as it has gone (#164, the author's approval). */
export function pinRow(label: string, value: number, right: string, action = ''): string {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return `<div class="sd-pin sd-fill" style="--p:${pct}%">
    <div class="sd-pin__line"><b>${escapeHtml(label)}</b><span class="sd-label">${escapeHtml(right)} · ${pct}%</span>${action}</div>
  </div>`;
}

// ---------- research ----------

/** The research tab's pinned top: what is being learnt now. */
export function researchPinned(locale: Locale, view: ResearchView): string {
  const head = `<div class="sd-section__head"><h2>${t(locale, 'research.title')}</h2><span class="sd-label">${t(locale, 'research.slots', { used: view.slots.used, total: view.slots.total })} · ${t(locale, 'research.perCycle', { n: num(locale, view.perCycle, 1), stock: num(locale, view.stock) })}</span></div>`;
  const rows = view.active.length
    ? view.active
        .map((a) => {
          const n = view.nodes.find((x) => x.id === a.id)!;
          return pinRow(
            a.name,
            a.value,
            a.eta,
            actionButton(t(locale, 'research.cancel'), n.command, null),
          );
        })
        .join('')
    : `<p class="sd-label">${t(locale, 'research.none')}</p>`;
  return `${head}${rows}`;
}

/** A green dot on what can be done right now (#152). */
export const canDot = '<i class="sd-can" aria-hidden="true"></i>';

/** A pop-up card beside the panel (#152): a title, and what to do with the thing picked. */
export interface PopCard {
  readonly title: string;
  readonly sub: string;
  readonly body: string;
}

/**
 * The tree, compact (#152): a node is its name, how far along it is and a dot when it
 * can be started; what it gives and costs opens in a card beside the panel.
 */
export function researchTab(locale: Locale, view: ResearchView, picked: TechId | null): string {
  const node = (n: TechNode) => {
    const progress = n.status === 'active' || n.progress > 0 ? n.progress / n.price : 0;
    const can = n.status === 'open' && n.command !== null;
    // Progress fills the tile itself (#156): it keeps its height.
    const fill = progress > 0 ? Math.max(3, Math.min(100, Math.round(progress * 100))) : 0;
    const pct = fill ? ` · ${fill}%` : '';
    return `<button type="button" class="sd-panel sd-tech sd-tech--${n.status} ${n.tier === 'final' ? 'sd-tech--final' : ''} ${n.id === picked ? 'sd-tech--picked' : ''} ${fill ? 'sd-fill' : ''}" ${fill ? `style="--p:${fill}%"` : ''} data-tech="${n.id}" title="${escapeHtml([n.name + pct, ...n.does].join(' · '))}">
      ${can ? canDot : ''}<span>${escapeHtml(n.name)}</span>
    </button>`;
  };
  const final = view.nodes.find((n) => n.tier === 'final');
  const grid = view.branches
    .map((b) => {
      const cells = ([1, 2, 3] as const)
        .map(
          (tier) =>
            `<div class="sd-tree__cell">${view.nodes
              .filter((n) => n.branch === b.id && n.tier === tier)
              .map(node)
              .join('')}</div>`,
        )
        .join('');
      return `<div class="sd-tree__branch" title="${escapeHtml(b.name)}">${t(locale, `techBranch.${b.id}.short`)}</div>${cells}`;
    })
    .join('');
  return `
    <section class="sd-section">
      <div class="sd-tree" role="group" aria-label="${t(locale, 'tab.research')}">
        <span></span>${[1, 2, 3].map((n) => `<span class="sd-tree__tier">${t(locale, 'research.tier', { tier: n })}</span>`).join('')}
        ${grid}
        ${final ? `<div class="sd-tree__branch">${t(locale, 'techBranch.final.short')}</div>${node(final)}` : ''}
      </div>
      <p class="sd-label sd-hint">${escapeHtml(view.hint)}</p>
    </section>`;
}

/** What a technology gives and costs, and the button to start it. */
export function researchPop(locale: Locale, view: ResearchView, id: TechId): PopCard | null {
  const pick = view.nodes.find((n) => n.id === id);
  if (!pick) return null;
  const body = `${pick.requires ? `<div class="sd-label">${escapeHtml(pick.requires)}</div>` : ''}
    <ul class="sd-does">${pick.does.map((d) => `<li>${escapeHtml(d)}</li>`).join('')}</ul>
    ${
      pick.status === 'known'
        ? `<div class="sd-label">${t(locale, 'research.known')}</div>`
        : `<div class="sd-offer"><span class="sd-cost"><span>${t(locale, 'research.cost', { n: num(locale, pick.price) })}</span>${pick.exotics ? `<span>${goodIcon('exotics')}${pick.exotics}</span>` : ''}${pick.knownBy ? `<span class="sd-muted">${t(locale, 'research.diffused', { n: pick.knownBy })}</span>` : ''}${pick.discount ? `<span class="sd-muted">${t(locale, 'research.fragments', { pct: Math.round(pick.discount * 100) })}</span>` : ''}</span>${actionButton(
            t(locale, pick.status === 'active' ? 'research.cancel' : 'research.start'),
            pick.command,
            pick.reason,
            pick.status === 'active' ? '' : 'primary',
          )}</div>`
    }`;
  return {
    title: pick.name,
    sub: t(locale, 'research.tier', {
      tier: pick.tier === 'final' ? t(locale, 'techBranch.final.name') : pick.tier,
    }),
    body,
  };
}

// ---------- fleet ----------

/** The shipyard's queue, pinned over the yard. */
export function fleetPinned(locale: Locale, view: FleetView): string {
  const head = `<div class="sd-label">${t(locale, 'fleet.count', { n: view.ships, limit: view.limit })} · ${t(locale, 'fleet.upkeep', { cash: num(locale, view.upkeep.cash), alloys: num(locale, view.upkeep.alloys, 1), energy: num(locale, view.upkeep.energy, 1) })}</div>`;
  const rows = view.queue.length
    ? view.queue
        .map((q) =>
          q.building
            ? pinRow(q.name, q.value, t(locale, 'fleet.building', { left: q.left }))
            : `<div class="sd-label">${escapeHtml(q.name)} · ${t(locale, 'fleet.waiting')}</div>`,
        )
        .join('')
    : `<p class="sd-label">${t(locale, view.parallel ? 'fleet.queueEmpty' : 'fleet.noSpaceport')}</p>`;
  return `${rows}${head}`;
}

/** The yard (#152): a tile per class, its card beside the panel. */
export function fleetYard(locale: Locale, view: FleetView, picked: string | null): string {
  const tiles = view.options
    .map(
      (
        o,
      ) => `<button type="button" class="sd-panel sd-ship ${o.command ? '' : 'sd-ship--no'} ${o.id === picked ? 'sd-ship--picked' : ''}" data-ship="${o.id}" title="${escapeHtml(o.reason ?? o.stats)}">
        ${o.command ? canDot : ''}<span class="sd-ship__name">${shipIcon(o.id)}<b>${escapeHtml(o.name)}</b></span>
        <span class="sd-cost"><span>${num(locale, o.cash)}</span><span>${goodIcon('alloys')}${num(locale, o.alloys)}</span><span class="sd-muted">${o.time}</span></span>
        ${o.reason ? `<small class="sd-ship__why">${escapeHtml(o.reason)}</small>` : ''}
      </button>`,
    )
    .join('');
  return `<section class="sd-section">
      <div class="sd-ships">${tiles}</div>
      <p class="sd-label sd-hint">${t(locale, 'fleet.yardHint')}</p>
    </section>`;
}

export function fleetPop(locale: Locale, view: FleetView, id: string): PopCard | null {
  const o = view.options.find((x) => x.id === id);
  if (!o) return null;
  return {
    title: o.name,
    sub: '',
    body: `<div class="sd-muted">${escapeHtml(o.stats)}</div>
      <div class="sd-offer"><span class="sd-cost"><span>${num(locale, o.cash)}</span><span>${goodIcon('alloys')}${num(locale, o.alloys)}</span><span class="sd-muted">${o.time}</span></span>${actionButton(t(locale, 'fleet.lay'), o.command, o.reason, 'primary')}</div>`,
  };
}

/** The ships (#115): one compact row per fleet, its stance and orders in one line. */
export function fleetShips(
  locale: Locale,
  view: FleetView,
  hit: ReadonlySet<number> = new Set(),
): string {
  const fleets = view.fleets.length
    ? view.fleets
        .map(
          (
            f,
          ) => `<article class="sd-fleet ${hit.has(f.id) ? 'sd-fleet--hit' : ''}" data-fleet="${f.id}">
            <div class="sd-fleet__head">
              ${hit.has(f.id) ? `<i class="sd-hit" title="${t(locale, 'units.hit')}">!</i>` : ''}${f.ready ? canDot : ''}${shipIcon(f.lead)}<b>${escapeHtml(f.title)}</b>
              <span class="sd-fleet__ships">${f.single ? '' : escapeHtml(f.ships)}</span>
              <span class="sd-fleet__hull ${f.hull < 50 ? 'sd-down' : ''}">${f.hull}%</span>
              <button class="sd-btn sd-btn--icon sd-btn--small" type="button" data-focus="${f.id}" title="${t(locale, 'fleet.show')}">◎</button>
            </div>
            <div class="sd-label">${escapeHtml(f.where)} · ${escapeHtml(f.busy ?? f.motion)}${f.armed ? '' : ` · ${escapeHtml(f.retreat)}`}</div>
            <div class="sd-fleet__row">
              ${f.armed ? `<span class="sd-segmented sd-fleet__stance" title="${escapeHtml(f.retreat)}">` : '<span hidden>'}${f.stances
                .map(
                  (s) =>
                    `<button class="sd-btn sd-btn--small" type="button" aria-pressed="${s.id === f.stance}" ${s.command ? `data-command="${cmd(s.command)}"` : ''}>${escapeHtml(s.label)}</button>`,
                )
                .join('')}</span>
              ${[...f.actions, ...f.splits, ...f.merges.filter((m) => m.command)]
                .filter((a) => a.command)
                .map(
                  (a) =>
                    `<button class="sd-btn sd-btn--small ${a.primary ? 'sd-btn--primary' : ''}" type="button" data-command="${cmd(a.command!)}">${escapeHtml(a.label)}</button>`,
                )
                .join('')}
            </div>
            ${
              // Why ships here do not merge (#160): once, under the row.
              f.merges.length && !f.merges.some((m) => m.command) && f.merges[0]!.reason
                ? `<div class="sd-label">${escapeHtml(f.merges[0]!.reason)}</div>`
                : ''
            }
          </article>`,
        )
        .join('')
    : `<p class="sd-label">${t(locale, 'fleet.none')}</p>`;
  return `
    <section class="sd-section">
      <p class="sd-label">${t(locale, 'fleet.fleetsHint')}</p>
      ${fleets}
    </section>`;
}

/** Sub-tabs (#152): a row of buttons, each with the count of what can be done there. */
export function subTabs(
  items: readonly { readonly id: string; readonly label: string; readonly count: number }[],
  on: string,
): string {
  return `<div class="sd-subtabs" role="tablist">${items
    .map(
      (x) =>
        `<button type="button" role="tab" class="sd-subtab" data-sub="${x.id}" aria-selected="${x.id === on}">${escapeHtml(x.label)}${x.count ? `<i class="sd-count">${x.count}</i>` : ''}</button>`,
    )
    .join('')}</div>`;
}

// ---------- diplomacy ----------

export function diplomacyTab(locale: Locale, view: DiplomacyView): string {
  const rivals = view.rivals
    .map(
      (r) => `<article class="sd-rival ${r.hostile ? 'sd-rival--hostile' : ''}">
        <div class="sd-rival__head">
          <span class="sd-emblem sd-emblem--sm" style="--seat:${r.color}"></span>
          <b>${escapeHtml(r.name)}</b><small class="sd-muted">${escapeHtml(r.faction)}</small>
          <span class="sd-badge ${r.hostile ? 'sd-badge--alert' : ''}">${escapeHtml(r.status)}</span>
        </div>
        <div class="sd-label">${t(locale, 'diplomacy.rivalStats', { prestige: num(locale, r.prestige, 1), reputation: num(locale, r.reputation) })}</div>
        ${r.tags.length ? `<div class="sd-tags">${r.tags.map((x) => `<span class="sd-badge sd-badge--heat">${escapeHtml(x)}</span>`).join('')}</div>` : ''}
        ${
          r.agreements.length
            ? `<div class="sd-chips">${r.agreements
                .map(
                  (a) =>
                    `<span class="sd-chip">${escapeHtml(a.name)}<button type="button" class="sd-chip__x" data-command="${cmd(a.cancel)}" title="${t(locale, 'diplomacy.cancel')}" aria-label="${t(locale, 'diplomacy.cancel')}">×</button></span>`,
                )
                .join('')}</div>`
            : ''
        }
        ${r.incoming.length ? `<div class="sd-rival__actions">${r.incoming.map(act).join('')}</div>` : ''}
        ${r.outgoing.length ? `<div class="sd-label">${r.outgoing.map(escapeHtml).join(' · ')}</div>` : ''}
        ${r.actions.length ? `<div class="sd-rival__actions">${r.actions.map(act).join('')}</div>` : ''}
        ${r.sanctions.length ? `<div class="sd-rival__actions">${r.sanctions.map(act).join('')}</div>` : ''}
      </article>`,
    )
    .join('');
  const projects = view.projects.length
    ? view.projects
        .map(
          (p) => `<article class="sd-project">
            <div class="sd-project__line"><b>${escapeHtml(p.name)}</b><span class="sd-muted">${t(locale, 'diplomacy.project.left', { crisis: escapeHtml(p.crisis), time: p.left })}</span></div>
            <div class="sd-project__track">${p.parts
              .map((x) => `<span style="--seat:${x.color};width:${x.share * 100}%"></span>`)
              .join(
                '',
              )}<span class="sd-project__mark" style="left:${(p.threshold / p.full) * 100}%"></span></div>
            <div class="sd-project__foot"><span class="sd-muted">${t(locale, 'diplomacy.project.progress', { total: num(locale, p.total), threshold: num(locale, p.threshold), full: num(locale, p.full), mine: num(locale, p.mine) })}</span></div>
            <div class="sd-label">${escapeHtml(p.status)} · ${escapeHtml(p.reward)}</div>
            <div class="sd-rival__actions">${p.gifts.map(act).join('')}</div>
          </article>`,
        )
        .join('')
    : `<p class="sd-label">${t(locale, 'diplomacy.projectsNone')}</p>`;
  return `
    <section class="sd-section">
      <div class="sd-section__head"><h2>${t(locale, 'diplomacy.title')}</h2><span class="sd-label">${t(locale, 'diplomacy.reputation', { n: num(locale, view.reputation) })}</span></div>
      <p class="sd-label">${escapeHtml(view.standing)}</p>
      ${view.independence ? act(view.independence) : ''}
      ${rivals}
    </section>
    <section class="sd-section">
      <div class="sd-section__head"><h2>${t(locale, 'diplomacy.projects')}</h2><span class="sd-label">${t(locale, 'diplomacy.projectsHint')}</span></div>
      ${projects}
    </section>`;
}
